import React, { useContext, useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    FileUploader,
    IconInput,
    Input,
    RsDatePicker,
    Select,
    TagsInputs,
    UserNameInput,
} from "../../Components";
import { GroupRadios } from "../../Components/GroupRadios";
import { useApi } from "../../Helpers/ApiHook";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { AuthContext } from "../../Contexts/AuthContext";
import { FILE_SELECTION_LIMIT, FILE_SIZES, validationSchema } from "./validations";
import YearPicker from "../../Components/YearPicker";
import { TitleSection } from "../GymFacilities/components";
import CustomAlert from "../../Components/CustomAlert";

const OPTIONS = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
];

const INSTITUTION_TYPES_OPTIONS = [
    {
        value: "Fitness Academy",
        label: "Fitness Academy",
        checked: true,
        disabled: false,
    },
    {
        value: "Sports Education Institute",
        label: "Sports Education Institute",
        checked: false,
        disabled: false,
    },
    {
        value: "Health & Wellness Institute",
        label: "Health & Wellness Institute",
        checked: false,
        disabled: false,
    },
    {
        value: "Other (please specify)",
        label: "Other (please specify)",
        checked: false,
        disabled: false,
    },
];

const LEGAL_STATUS_OPTIONS = [
    {
        value: "Proprietorship",
        label: "Proprietorship",
    },
    {
        value: "Partnership",
        label: "Partnership",
    },
    {
        value: "LLP",
        label: "LLP",
    },
    {
        value: "Pvt Ltd",
        label: "Pvt Ltd",
    },
    {
        value: "Trust / Society",
        label: "Trust / Society",
    },
    {
        value: "Other (please specify)",
        label: "Other (please specify)",
    },
];

const DEFAULT_VALUES = {
    // Institutional Details
    academy_name: "",
    user_name: "",
    institution_type: "Fitness Academy",
    est_year: "",
    legal_status: "Partnership",
    pan_no: "",

    // Contact / address – like gym
    full_address: "",
    country_id: "",
    state_id: "",
    city_id: "",
    pincode: "",
    email: "",
    phone_no: "",
    website_url: "",

    // Social links (DON'T change pattern)
    social_links: {
        instagram: "",
        facebook: "",
        linkedin: "",
        youtube: "",
    },

    // Authorized person details (DON'T change pattern)
    authorized_person_deatils: {
        name: "",
        designation: "",
        resedential_address: "",
        Staff_Details: "",
    },

    // Courses & affiliations (DON'T change pattern)
    courses_and_affiliations: {
        courses: [],      // tags
        branches: [],     // tags
        affiliation_status: "Yes",
    },

    // Infrastructure & facilities (DON'T change pattern)
    infrastructure_facilities: {
        buildup_area: "",
        lab_facilities: "",
        staff_count: "",
        tools_used: "",
    },

    // Files
    logo_img: null,
    registration_docs: null,
    pan_card: null,
    course_brochure: null,
    lab_img: [], // multiple images
    previous_affiliations_doc: null,
    faculty_qualification: null,

    // Other fields used in form
    declaration_agree: false,
    declaration: {
        signatory_name: "",
        designation: "",
        date: null,
    },
    signature_img: null,
};

export function CreateTrainingProvider() {

    const location = useLocation();
    const navigate = useNavigate();

    const { getUserDetails, user } = useContext(AuthContext);

    const isMyProfilePage = location.pathname === '/my-profile';
    const user_id = 1 || user?.training_provider_details?.id || null;

    const { get, postFormData } = useApi();

    const [formData, setFormData] = useState(DEFAULT_VALUES);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [countries, setCountries] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoadingState, setIsLoadingState] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [isValidUserName, setValidUserName] = useState(false);

    const _getTrainingProviderDetails = async () => {
        setIsLoading(true);
        try {
            const data = await get(`trainingprovider/show/${user_id}`);
            if (data?.status) {
                setFormData({
                    ...DEFAULT_VALUES,
                    ...data?.data,
                    ...data?.data?.social_links,
                    ...data?.data?.location,
                    documents: { ...data?.data?.documents, profile_img: data?.data?.profile_img, banner_img: data?.data?.banner_img },
                    profile_img: "",
                    banner_img: "",
                    codeOfEthics: true,
                    user_name: data?.data?.user_name || ""
                })
                if (!!data?.data?.user_name) {
                    setValidUserName(true);
                }
            }
        } catch (error) {
            console.error("Error fetching trainers: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isMyProfilePage) {
            _getTrainingProviderDetails();
        }
        _getCountries();
    }, []);

    useEffect(() => {
        if (formData.country_id) {
            _getStates();
        } else {
            setStateList([]);
            setCities([]);
        }
    }, [formData.country_id]);

    useEffect(() => {
        if (formData.country_id && formData.state_id) {
            _getCities();
        } else {
            setCities([]);
        }
    }, [formData.state_id]);

    const _getCountries = async () => {
        setIsLoading(true);
        try {
            const data = await get("countries/list", { params: {} });
            if (data.status) setCountries(data.data || []);
        } catch (e) {
            console.error("Error fetching countries: ", e);
        } finally {
            setIsLoading(false);
        }
    };

    const _getStates = async () => {
        setIsLoadingState(true);
        try {
            const data = await get(`states/${formData.country_id}`, {});
            if (data.status) setStateList(data.data || []);
        } catch (e) {
            console.error("Error fetching states: ", e);
        } finally {
            setIsLoadingState(false);
        }
    };

    const _getCities = async () => {
        setIsLoadingCities(true);
        try {
            const data = await get(`cities/${formData.state_id}`, {});
            if (data.status) setCities(data.data || []);
        } catch (e) {
            console.error("Error fetching cities: ", e);
        } finally {
            setIsLoadingCities(false);
        }
    };

    // ------------ Handlers ------------

    const handleChange = (e, regex = null) => {
        const { name, value, type, checked } = e.target;
        const finalValue =
            type === "checkbox"
                ? checked
                : regex
                    ? (value || "").replace(regex, "")
                    : value;

        setFormData((prev) => ({
            ...prev,
            [name]: finalValue,
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleRadioChange = (event) => {
        const name = event.target.name || "";
        const val = event.target.value || "";
        setFormData((prev) => ({
            ...prev,
            [name]: val,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleFileChange = (name, files) => {
        let value = null;
        if ((files instanceof File || value instanceof Blob) || Array.isArray(files)) {
            value = files; // FileList (single or multiple)
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };


    const appendFile = (form, key, value) => {
        if (!value) return;

        if (value instanceof File || value instanceof Blob) {
            form.append(key, value, value.name);
        } else if (Array.isArray(value)) {
            value.forEach((file) => {
                if (file) form.append(key, file, file.name);
            });
        } else if (typeof FileList !== "undefined" && value instanceof FileList) {
            Array.from(value).forEach((file) => {
                if (file) form.append(key, file, file.name);
            });
        }
    };

    const onSubmitTrainingProvider = async () => {
        try {

            const trainingProviderFormData = new FormData();

            Object.entries({ ...formData }).forEach(([key, value]) => {

                const appendValue = (path = "", val = "") => {
                    if (val instanceof File || val instanceof Blob) {
                        trainingProviderFormData.append(path, val, val.name); // ✅ use filename here
                    } else if (typeof val === "object" && val !== null) {
                        trainingProviderFormData.append(path, JSON.stringify(val));
                    } else {
                        trainingProviderFormData.append(path, "" + (val ?? ""));
                    }
                };

                const objectAppend = (path = "", val = {}) => {
                    if (Array.isArray(val)) {
                        val.forEach((item, i) => {
                            if (typeof item === 'object') {
                                objectAppend(`${path}[${i}]`, item)
                            } else {
                                appendValue(`${path}[${i}]`, item);
                            }
                        });
                    } else if (val instanceof File || val instanceof Blob) {
                        appendValue(`${path}`, val || null);
                    } else {
                        Object.entries({ ...val }).forEach(([subKey, subVal]) => {
                            if (typeof subVal === 'object') {
                                objectAppend(`${path}[${subKey}]`, subVal)
                            } else {
                                appendValue(`${path}[${subKey}]`, subVal);
                            }
                        });
                    }
                }

                if (Array.isArray(value)) {
                    value.forEach((item, i) => {
                        if (Array.isArray(item)) {
                            item.forEach((item, i) => {
                                if (typeof item === 'object') {
                                    objectAppend(`${key}[${i}]`, item)
                                } else {
                                    appendValue(`${key}[${i}]`, item);
                                }
                            });
                        } else if (typeof item === 'object') {
                            objectAppend(`${key}[${i}]`, item)
                        } else {
                            appendValue(`${key}[${i}]`, item);
                        }
                    });
                } else if (typeof value === 'object' && value !== null) {
                    objectAppend(`${key}`, value || "")
                } else {
                    appendValue(key, value);
                }
            });

            const urlPath = (isMyProfilePage ? `training-provider/update/${user_id}` : "trainingprovider/store");

            const response = await postFormData(urlPath, trainingProviderFormData);
            if (!!response?.status) {
                if (isMyProfilePage) {
                    CustomAlert({
                        type: "success",
                        title: "My Profile Update",
                        message: 'Training Provider profile successfully updated.',
                        confirmText: 'Okay',
                        onConfirm: (e) => {
                            onUpdateLocalData()
                        }
                    })
                } else {
                    CustomAlert({
                        type: "success",
                        title: "Training Provider Create",
                        message: 'Training Provider successfully created',
                        confirmText: 'Okay',
                        onConfirm: (e) => {
                            onUpdateLocalData()
                        }
                    })
                }
            }
        } catch (e) {
            console.log(e)
        } finally {
            setIsSubmitting(false)
        }
    }

    const onUpdateLocalData = async () => {
        await navigate(-1);
        await getUserDetails()
    }
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        await setIsSubmitting(true)
        try {
            // console.log(formData)
            await validationSchema.validate(formData, { abortEarly: false });
            // console.log('Form Data:', formData);
            await setErrors({});
            await onSubmitTrainingProvider();
            // alert('Form submitted successfully!');
            // Handle form submission here
        } catch (err) {
            const validationErrors = {};
            err.inner.forEach(error => {
                validationErrors[error.path] = error.message;
            });
            // console.log(validationErrors)
            setErrors(validationErrors);
            setIsSubmitting(false)
        }
    };
    const handleReset = () => {
        setFormData(DEFAULT_VALUES);
        setErrors({});
    };

    // ------------ JSX ------------

    return (
        <div className="w-full bg-gradient-radial flex flex-col">
            <TitleSection
                title="FWPI Training Provider Application Form"
                subtitle="For Academies and Educational Institutes Only"
            />

            <div className="w-full bg-gradient-radial">
                <div className="container mx-auto px-4 lg:px-10">
                    <form
                        className="max-w-6xl font-urbanist rounded-lg mx-auto space-y-8 bg-[#181B23] text-white py-12 px-12 lg:px-32"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        {/* ==================== Institutional Details ==================== */}
                        <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                            Institutional Details :-
                        </p>

                        <Input
                            label="Name of the Academy/Institute"
                            subTitle="(Gym, Studio, Wellness Center, etc.)"
                            id="academy_name"
                            name="academy_name"
                            placeholder="Enter Your Name of the Academy"
                            value={formData.academy_name}
                            onChange={handleChange}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        <UserNameInput
                            user_id={user_id || null}
                            category="training-provider"
                            value={formData.user_name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            onStatusChange={({ status, isExist, username }) => {
                                // console.log("status", status, "isExist", isExist, "username", username);
                                if (isExist === true) {
                                    setValidUserName(true);
                                } else {
                                    setValidUserName(false);
                                }
                                // isExist === true  => username exists (taken)
                                // isExist === false => username available
                                // isExist === null  => other states (tooShort, checking, invalid, error, idle)
                            }}
                        />

                        <GroupRadios
                            label="Type of Institution"
                            required={true}
                            name={"institution_type"}
                            value={formData.institution_type}
                            options={INSTITUTION_TYPES_OPTIONS}
                            onChange={handleRadioChange}
                            errors={errors}
                        />

                        <YearPicker
                            label="Year of Establishment"
                            id="est_year"
                            name="est_year"
                            minYear={'1950'}
                            maxYear={moment().format('YYYY')}
                            rows={3}
                            columns={3}
                            placeholder="Select year"
                            value={formData.est_year || ""}
                            disabled={isSubmitting}
                            errors={errors}
                            onChange={(year) => {
                                setFormData((prevState) => ({ ...prevState, est_year: year }));
                            }}
                        />

                        <GroupRadios
                            label="Legal Status of the Institution"
                            required={true}
                            name={"legal_status"}
                            value={formData.legal_status}
                            options={LEGAL_STATUS_OPTIONS}
                            onChange={handleRadioChange}
                            errors={errors}
                        />

                        <Input
                            label="PAN Number"
                            id="pan_no"
                            name="pan_no"
                            placeholder="Enter Your PAN Number"
                            value={formData.pan_no}
                            onChange={handleChange}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        {/* ==================== Contact Information + Address ==================== */}
                        <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                            Contact Information :-
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 items-center">
                            <Input
                                label="Institutional Email ID"
                                id="email"
                                name="email"
                                placeholder="Enter Your Institutional Email ID"
                                value={formData.email}
                                onChange={handleChange}
                                errors={errors}
                                required
                                disabled={isSubmitting}
                            />

                            <IconInput
                                src="https://flagcdn.com/in.svg"
                                label="Official Contact Number"
                                id="phone_no"
                                name="phone_no"
                                placeholder="Enter Your Official Contact Number"
                                type="tel"
                                value={formData.phone_no}
                                onChange={(e) => handleChange(e, /[^0-9]/)}
                                errors={errors}
                                maxLength={10}
                                required
                                disabled={isSubmitting}
                            />
                        </div>

                        <Input
                            label="Website (if any)"
                            id="website_url"
                            name="website_url"
                            placeholder="Enter Your Website (if any)"
                            value={formData.website_url}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        {/* Address like Gym form */}
                        <div className="w-full flex flex-col justify-center gap-x-6 gap-y-6">
                            <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                                Address :-
                            </p>

                            <Input
                                type="textarea"
                                label="Official Registered Address"
                                id="full_address"
                                name="full_address"
                                placeholder="Enter Your Official Registered Address"
                                value={formData.full_address}
                                onChange={handleChange}
                                errors={errors}
                                required
                                disabled={isSubmitting}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                                <Select
                                    label="Country"
                                    id="country_id"
                                    name="country_id"
                                    placeholder="Select Country"
                                    onChange={handleChange}
                                    options={countries}
                                    schema={{ name: "name", value: "id" }}
                                    loading={isLoading}
                                    errors={errors}
                                    value={formData.country_id}
                                    required
                                    disabled={isSubmitting}
                                />

                                <Select
                                    label="State"
                                    id="state_id"
                                    name="state_id"
                                    placeholder="Select State"
                                    onChange={handleChange}
                                    options={stateList}
                                    schema={{ name: "name", value: "id" }}
                                    loading={isLoadingState}
                                    errors={errors}
                                    value={formData.state_id}
                                    required
                                    disabled={isSubmitting}
                                />

                                <Select
                                    label="City"
                                    id="city_id"
                                    name="city_id"
                                    placeholder="Select City"
                                    onChange={handleChange}
                                    options={cities}
                                    schema={{ name: "name", value: "id" }}
                                    loading={isLoadingCities}
                                    errors={errors}
                                    value={formData.city_id}
                                    required
                                    disabled={isSubmitting}
                                />

                                <Input
                                    label="Area PIN Code"
                                    id="pincode"
                                    name="pincode"
                                    placeholder="Enter Your Area PIN Code"
                                    value={formData.pincode}
                                    onChange={(e) => handleChange(e, /[^0-9]/)}
                                    errors={errors}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {/* ==================== Social Media Links ==================== */}
                        <div className="w-full flex flex-col justify-center gap-x-6 gap-y-6">
                            <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                                Social Media Links :-
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                                {Object.keys(DEFAULT_VALUES.social_links).map(
                                    (urlKey, index) => (
                                        <Input
                                            key={index}
                                            label={`${_.capitalize(urlKey)} URL`}
                                            id={urlKey}
                                            name={urlKey}
                                            type="url"
                                            placeholder={`Enter Your ${_.capitalize(
                                                urlKey
                                            )} URL`}
                                            value={
                                                formData?.social_links?.[urlKey] || ""
                                            }
                                            onChange={(event) => {
                                                const name = event.target.name || "";
                                                const value = event.target.value || "";
                                                setFormData((prevState) => ({
                                                    ...prevState,
                                                    social_links: {
                                                        ...prevState.social_links,
                                                        [name]: value,
                                                    },
                                                }));
                                            }}
                                            errors={errors}
                                            disabled={isSubmitting}
                                        />
                                    )
                                )}
                            </div>
                        </div>

                        {/* ==================== Authorized Person ==================== */}
                        <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                            Authorized Person :-
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 items-center">
                            <Input
                                label="Name of Authorized Person / Partner"
                                id="authorized_person_name"
                                name="authorized_person_name"
                                placeholder="Enter Name of Authorized Person / Partner"
                                value={formData.authorized_person_deatils.name}
                                errorName='authorized_person_deatils.name'
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormData((prev) => ({
                                        ...prev,
                                        authorized_person_deatils: {
                                            ...prev.authorized_person_deatils,
                                            name: val,
                                        },
                                    }));
                                }}
                                errors={errors}
                                required
                                disabled={isSubmitting}
                            />
                            <Input
                                label="Designation"
                                id="authorized_person_designation"
                                name="authorized_person_designation"
                                placeholder="Enter Designation"
                                value={formData.authorized_person_deatils.designation}
                                errorName='authorized_person_deatils.designation'
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormData((prev) => ({
                                        ...prev,
                                        authorized_person_deatils: {
                                            ...prev.authorized_person_deatils,
                                            designation: val,
                                        },
                                    }));
                                }}
                                errors={errors}
                                required
                                disabled={isSubmitting}
                            />
                        </div>

                        <Input
                            label="Residential Address of Authorized Person"
                            id="authorized_person_address"
                            name="authorized_person_address"
                            placeholder="Enter Residential Address of Authorized Person"
                            value={formData.authorized_person_deatils.resedential_address}
                            errorName='authorized_person_deatils.resedential_address'
                            onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                    ...prev,
                                    authorized_person_deatils: {
                                        ...prev.authorized_person_deatils,
                                        resedential_address: val,
                                    },
                                }));
                            }}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        <Input
                            label="Teaching Staff Details"
                            subTitle="(Please mention Name, Designation, and Qualification of each faculty member)"
                            id="teaching_staff_details"
                            name="teaching_staff_details"
                            placeholder="Enter Teaching Staff Details"
                            value={formData.authorized_person_deatils.Staff_Details}
                            errorName='authorized_person_deatils.Staff_Details'
                            onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                    ...prev,
                                    authorized_person_deatils: {
                                        ...prev.authorized_person_deatils,
                                        Staff_Details: val,
                                    },
                                }));
                            }}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        {/* ==================== Courses & Affiliations ==================== */}
                        <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                            Courses & Affiliations :-
                        </p>

                        {/* TAGS: Courses */}
                        <TagsInputs
                            label="Courses to be Affiliated"
                            subTitle="(Specify course name and duration)"
                            id="courses_tags"
                            name="courses_tags"
                            placeholder="Write course and press enter to add"
                            value={formData.courses_and_affiliations.courses || []}
                            errorName='courses_and_affiliations.courses'
                            onChange={(tags) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    courses_and_affiliations: {
                                        ...prev.courses_and_affiliations,
                                        courses: tags,
                                    },
                                }));
                            }}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        <GroupRadios
                            label="Is the Institute Affiliated with any Other Certifying Body?"
                            required={true}
                            name={"affiliation_status"}
                            value={formData.courses_and_affiliations.affiliation_status}
                            errorName='courses_and_affiliations.affiliation_status'
                            options={OPTIONS}
                            onChange={(val) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    courses_and_affiliations: {
                                        ...prev.courses_and_affiliations,
                                        affiliation_status: val,
                                    },
                                }));
                            }}
                        />

                        {/* TAGS: Branches */}
                        <TagsInputs
                            label="Specify Institute Branches"
                            subTitle="(if any)"
                            id="branches_tags"
                            name="branches_tags"
                            placeholder="Write branch and press enter to add"
                            value={formData.courses_and_affiliations.branches || []}
                            errorName='courses_and_affiliations.branches'
                            onChange={(tags) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    courses_and_affiliations: {
                                        ...prev.courses_and_affiliations,
                                        branches: tags,
                                    },
                                }));
                            }}
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        {/* ==================== Infrastructure & Facilities ==================== */}
                        <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                            Infrastructure & Facilities :-
                        </p>

                        <Input
                            label="Total Built-Up Area"
                            subTitle="(in sq. ft.)"
                            id="buildup_area"
                            name="buildup_area"
                            placeholder="Enter Total Built-Up Area"
                            value={formData.infrastructure_facilities.buildup_area}
                            errorName="infrastructure_facilities.buildup_area"
                            onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                    ...prev,
                                    infrastructure_facilities: {
                                        ...prev.infrastructure_facilities,
                                        buildup_area: val,
                                    },
                                }));
                            }}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        <Input
                            label="Classroom & Lab Facilities"
                            id="class_lab_facilities"
                            name="class_lab_facilities"
                            placeholder="Enter Classroom & Lab Facilities"
                            value={formData.infrastructure_facilities.lab_facilities}
                            errorName="infrastructure_facilities.lab_facilities"
                            onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                    ...prev,
                                    infrastructure_facilities: {
                                        ...prev.infrastructure_facilities,
                                        lab_facilities: val,
                                    },
                                }));
                            }}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        <Input
                            label="Teaching/Training Staff Count"
                            id="staff_count"
                            name="staff_count"
                            placeholder="Enter Teaching/Training Staff Count"
                            value={formData.infrastructure_facilities.staff_count}
                            errorName="infrastructure_facilities.staff_count"
                            onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                    ...prev,
                                    infrastructure_facilities: {
                                        ...prev.infrastructure_facilities,
                                        staff_count: val,
                                    },
                                }));
                            }}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        <Input
                            label="Technological Tools Used"
                            subTitle="(e.g., LMS, Smart Boards, etc.)"
                            id="tools_used"
                            name="tools_used"
                            placeholder="Enter Technological Tools Used"
                            value={formData.infrastructure_facilities.tools_used}
                            errorName="infrastructure_facilities.tools_used"
                            onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                    ...prev,
                                    infrastructure_facilities: {
                                        ...prev.infrastructure_facilities,
                                        tools_used: val,
                                    },
                                }));
                            }}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        {/* ==================== Supporting Documents ==================== */}
                        <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                            Supporting Documents :-
                        </p>

                        <FileUploader
                            id="logo_img"
                            name="logo_img"
                            label="Logo Image"
                            accept=".png,.jpg,.jpeg"
                            maxFiles={1}
                            sizeInMb={FILE_SIZES.LOGO_IMG_FILE_SIZE}
                            required
                            onChange={(e) =>
                                handleFileChange("logo_img", e.target.files)
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        <FileUploader
                            id="registration_docs"
                            name="registration_docs"
                            label="Registration Certificate"
                            required
                            accept=".pdf"
                            maxFiles={1}
                            sizeInMb={FILE_SIZES.REGISTRATION_DOCS_FILE_SIZE}
                            onChange={(e) =>
                                handleFileChange("registration_docs", e.target.files)
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        <FileUploader
                            id="pan_card"
                            name="pan_card"
                            label="PAN Card"
                            required
                            accept=".pdf"
                            maxFiles={1}
                            sizeInMb={FILE_SIZES.PAN_CARD_FILE_SIZE}
                            onChange={(e) =>
                                handleFileChange("pan_card", e.target.files)
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        <FileUploader
                            label="Courses Brochure"
                            id="course_brochure"
                            name="course_brochure"
                            accept=".pdf"
                            sizeInMb={FILE_SIZES.COURSE_BROCHURE_FILE_SIZE}
                            required
                            maxFiles={1}
                            onChange={(e) =>
                                handleFileChange("course_brochure", e.target.files)
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        <FileUploader
                            label="Photographs of Classrooms/Labs"
                            id="lab_img"
                            name="lab_img"
                            accept=".png,.jpg,.jpeg"
                            required
                            value={formData.lab_img}
                            multiple={true}
                            maxFiles={FILE_SELECTION_LIMIT.LAB_IMAGES} // multiple images
                            sizeInMb={FILE_SIZES.LAB_IMG_FILE_SIZE}
                            onChange={(e) =>
                                handleFileChange("lab_img", e.target.files)
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        <FileUploader
                            label="Any Previous Affiliations/Collaborations"
                            id="previous_affiliations_doc"
                            name="previous_affiliations_doc"
                            accept=".pdf"
                            required
                            maxFiles={1}
                            sizeInMb={FILE_SIZES.PREVIOUS_AFFILIATIONS_DOC_FILE_SIZE}
                            onChange={(e) =>
                                handleFileChange(
                                    "previous_affiliations_doc",
                                    e.target.files
                                )
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        <FileUploader
                            label="Faculty List with Qualifications"
                            id="faculty_qualification"
                            name="faculty_qualification"
                            accept=".pdf"
                            required
                            maxFiles={1}
                            sizeInMb={FILE_SIZES.FACULTY_QUALIFICATION_FILE_SIZE}
                            onChange={(e) =>
                                handleFileChange(
                                    "faculty_qualification",
                                    e.target.files
                                )
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        {/* ==================== Declaration ==================== */}
                        <p className="mt-4 w-full text-md text-left text-3xl text-white py-2 font-urbanist border-b border-dashed">
                            Declaration :-
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 items-center">
                            <Input
                                label="Signatory Name"
                                id="signatory_name"
                                name="signatory_name"
                                placeholder="Enter Name of Authorised Signatory"
                                value={formData.declaration.signatory_name}
                                errorName='declaration.signatory_name'
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormData((prev) => ({
                                        ...prev,
                                        declaration: {
                                            ...prev.declaration,
                                            signatory_name: val,
                                        },
                                    }));
                                }}
                                errors={errors}
                                required
                                disabled={isSubmitting}
                            />
                            <Input
                                label="Designation"
                                id="designation"
                                name="designation"
                                placeholder="Enter Designation"
                                value={formData.declaration.designation}
                                errorName='declaration.designation'
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormData((prev) => ({
                                        ...prev,
                                        declaration: {
                                            ...prev.declaration,
                                            designation: val,
                                        },
                                    }));
                                }}
                                errors={errors}
                                required
                                disabled={isSubmitting}
                            />
                        </div>

                        <RsDatePicker
                            label="Date"
                            id="declaration_date"
                            name="declaration_date"
                            disableFutureDates={true}
                            value={!!formData.declaration.date ? moment(formData.declaration.date, "DD/MM/YYYY").toDate() : null}
                            errorName='declaration.date'
                            onChange={(date) => {
                                const val = !!date ? moment(date).format("DD/MM/YYYY") : null;
                                setFormData((prev) => ({
                                    ...prev,
                                    declaration: {
                                        ...prev.declaration,
                                        date: val,
                                    },
                                }));
                            }}
                            errors={errors}
                            disabled={isSubmitting}
                            required
                        />

                        <FileUploader
                            label="Signature Image"
                            id="signature_img"
                            name="signature_img"
                            accept=".png,.jpg,.jpeg"
                            maxFiles={1}
                            sizeInMb={FILE_SIZES.SIGNATURE_IMG_FILE_SIZE}
                            required
                            onChange={(e) =>
                                handleFileChange("signature_img", e.target.files)
                            }
                            errors={errors}
                            disabled={isSubmitting}
                        />

                        <Checkbox
                            id="declaration_agree"
                            name="declaration_agree"
                            className="mt-4"
                            label="I hereby declare that all information provided is true and correct to the best of my knowledge."
                            checked={formData.declaration_agree}
                            onChange={handleChange}
                            errors={errors}
                            required
                            disabled={isSubmitting}
                        />

                        {/* ==================== Buttons ==================== */}
                        <div className="flex justify-center gap-6 max-w-2xl mx-auto">
                            <Button
                                type="submit"
                                disabled={isValidUserName || !formData?.declaration_agree || isSubmitting}
                                loading={isSubmitting}
                            >
                                Submit
                            </Button>
                            {
                                !isMyProfilePage &&
                                <Button
                                    type="reset"
                                    disabled={isSubmitting}
                                    onClick={(e) => {
                                        handleReset(e)
                                    }}
                                >
                                    Clear Form
                                </Button>
                            }
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default CreateTrainingProvider;
